import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogDescription, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Plus, X, Check, Search, HandCoins, ArrowDown, Loader2, ArrowLeft, User, ChevronDown, Building2, Building } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import AddStudentForm from '@/components/agenda/AddStudentForm';
import AddCategoryForm from '@/components/agenda/AddCategoryForm';
import AddLocationForm from '@/components/agenda/AddLocationForm';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BASE_URL } from '@/config/api';
import { eventService } from '@/services/eventService';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion } from 'framer-motion';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { formatInTimeZone } from 'date-fns-tz';
import moment from 'moment-timezone';
import { CustomTimePicker } from '../CustomTimePicker';
import { CalendarRange } from '@/components/ui/CalendarRange';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

interface NewLessonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newLesson: {
    title: string;
    studentIds: string[];
    categoryId: string;
    locationId: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    isRecurring: boolean;
    recurrenceEndDate: string;
    note: string;
    price: number;
    extra: string;
  };
  setNewLesson: (newLesson: any) => void;
  isAllDay: boolean;
  setIsAllDay: (isAllDay: boolean) => void;
  students: any[];
  categories: any[];
  locations: any[];
  showAddStudentForm: boolean;
  setShowAddStudentForm: (show: boolean) => void;
  showAddCategoryForm: boolean;
  setShowAddCategoryForm: (show: boolean) => void;
  showAddLocationForm: boolean;
  setShowAddLocationForm: (show: boolean) => void;
  handleStudentAdded: (firstname: string, lastname: string, id: any) => void;
  handleCategoryAdded: (name: string, color: string, paymentType: number, prices: Record<string, number>, newCategoryId:any) => void;
  handleLocationAdded: (name: string) => void;
  handleCreateLesson: () => void;
}

const NewLessonDialog: React.FC<NewLessonProps> = ({
  open,
  onOpenChange,
  newLesson,
  setNewLesson,
  isAllDay,
  setIsAllDay,
  students,
  categories,
  locations,
  showAddStudentForm,
  setShowAddStudentForm,
  showAddCategoryForm,
  setShowAddCategoryForm,
  showAddLocationForm,
  setShowAddLocationForm,
  handleStudentAdded,
  handleCategoryAdded,
  handleLocationAdded,
  handleCreateLesson
}) => {
  const { t, locale } = useLanguage();
  const { user, token, refreshUserData } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [duration, setDuration] = useState(60);
  const [totalPrice, setTotalPrice] = useState(0);
  const [formType, setFormType] = useState<'lesson' | 'event'>('lesson');
  const [eventType, setEventType] = useState<'special_event' | 'student_absence' | 'coach_absence'>('special_event');
  const [recurrenceType, setRecurrenceType] = useState<'1' | '2' | '3' | '4'>('4');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStudentSelector, setShowStudentSelector] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showDuration, setShowDuration] = useState(false);



  const handleBackClick = () => {
    if (showAddStudentForm) {
      setShowAddStudentForm(false);
    } else if (showAddCategoryForm) {
      setShowAddCategoryForm(false);
    } else if (showAddLocationForm) {
      setShowAddLocationForm(false);
    } else if (showStudentSelector) {
      setShowStudentSelector(false);
    }
  };
  
  /*const handleDateSelect = (date) => {
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    setNewLesson({ ...newLesson, startDate: formattedDate, endDate: formattedDate });
  };*/

  const handleTimeChange = (newStartTime: string, newEndTime: string) => {
    const [startHours, startMinutes] = newStartTime.split(":").map(Number);
    const [endHours, endMinutes] = newEndTime.split(":").map(Number);

    // Utilisez une date de référence (par exemple, la date actuelle)
    const referenceDate = new Date(newLesson.startDate);
    const startDateTime = new Date(referenceDate);
    startDateTime.setHours(startHours, startMinutes, 0, 0);

    const endDateTime = new Date(referenceDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    // Calculer la durée en minutes
    let newDuration = Math.round((endDateTime.getTime() - startDateTime.getTime()) / 60000);

    if (newDuration <= 0) {
      newDuration = duration > 0 ? duration : 60;
      if (newEndTime < newStartTime) {
        const correctedStartDateTime = new Date(endDateTime.getTime() - newDuration * 60000);
        const correctedStartTime = format(correctedStartDateTime, "HH:mm");
        setNewLesson({
          ...newLesson,
          startTime: correctedStartTime,
          endTime: newEndTime,
        });
        setDuration(newDuration);
      } else {
        const correctedEndDateTime = new Date(startDateTime.getTime() + newDuration * 60000);
        const correctedEndTime = format(correctedEndDateTime, "HH:mm");
        setNewLesson({
          ...newLesson,
          startTime: newStartTime,
          endTime: correctedEndTime,
        });
        setDuration(newDuration);
      }
    } else {
      setNewLesson({
        ...newLesson,
        startTime: newStartTime,
        endTime: newEndTime,
      });
      setDuration(newDuration);
    }
  };

const ymdToLocalDate = (ymd: string) => {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatYMD = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};


  function utcToLocalDate(utcDateStr: string): Date {
    const [year, month, day] = utcDateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  function handleDateSelect(day?: Date) {
    if (!day) return;
    const year = day.getFullYear();
    const month = day.getMonth() + 1; 
    const date = day.getDate();
    const formattedDate = `${year}-${month.toString().padStart(2, "0")}-${date.toString().padStart(2, "0")}`;
    setNewLesson({
      ...newLesson,
      startDate: formattedDate, 
      endDate: isAllDay ? newLesson.endDate : formattedDate,
    });
  }


const handleDateSelectEnd = (day?: Date) => {
  if (!day) return;
  const start = newLesson.startDate ? ymdToLocalDate(newLesson.startDate) : undefined;

  if (!start || day.getTime() === start.getTime()) {
    // single-day → stocke exactement ce jour
    setNewLesson({ ...newLesson, endDate: formatYMD(day) });
  } else if (day.getTime() > start.getTime()) {
    // multi-day → stocke exclusive
    const end = new Date(day);
    end.setDate(end.getDate() + 1);
    setNewLesson({ ...newLesson, endDate: formatYMD(end) });
  }
};





  

  const renderStudentGuideAnimation = () => (
    <motion.div
      className="absolute -top-14 right-0 transform pointer-events-none flex flex-col items-center"
      animate={{
        y: [0, 8, 0],
        opacity: [1, 0.5, 1]
      }}
      transition={{
        duration: 1.9,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      <div className="bg-blue-50 rounded-lg p-2 mb-2 shadow-lg text-center text-blue-600 border border-blue-200">
        <p className="font-medium text-xs">
          {t('agenda.createFirstStudent')}
        </p>
      </div>
      <ArrowDown className="text-blue-500 w-8 h-8 drop-shadow-md animate-bounce" />
    </motion.div>
  );

  const renderCategoryGuideAnimation = () => (
    <motion.div
      className="absolute -top-12 right-0 transform pointer-events-none flex flex-col items-center"
      animate={{
        y: [0, 8, 0],
        opacity: [1, 0.5, 1]
      }}
      transition={{
        duration: 1.9,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      <div className="bg-blue-50 rounded-lg p-2 mb-2 shadow-lg text-center text-blue-600 border border-blue-200">
        <p className="font-medium text-xs">
          {t('agenda.createFirstCategory')}
        </p>
      </div>
      <ArrowDown className="text-blue-500 w-8 h-8 drop-shadow-md animate-bounce" />
    </motion.div>
  );

  const handleRemoveCategory = () => {
    setNewLesson({ ...newLesson, categoryId: '' });
  };

  const handleRemoveLocation = () => {
    setNewLesson({ ...newLesson, locationId: '' });
  };

  const handleRemoveStudents = () => {
    setNewLesson({ ...newLesson, studentIds: '' });
  };

  const selectedCategory = categories.find(cat => cat.id === newLesson.categoryId);
  const selectedLocation = locations.find(loc => loc.id === newLesson.locationId);

    useEffect(() => {
      if (formType === 'lesson') {
        setDuration(60);
      }
    }, [formType]);

  useEffect(() => {
  if (newLesson.startTime && newLesson.endTime && newLesson.startDate && newLesson.endDate) {
    const calculatedDuration = eventService.calculateDuration(
      newLesson.startTime,
      newLesson.endTime,
      newLesson.startDate,
      newLesson.endDate
    );
    setDuration(calculatedDuration);
  }
}, [newLesson.startTime, newLesson.endTime, newLesson.startDate, newLesson.endDate]);


// Pour afficher correctement le jour bleu
const getSelectedEndDateForCalendar = (endDate: string) => {
  const end = new Date(endDate);
  const start = new Date(newLesson.startDate);

  // Si single-day all-day → afficher le même jour
  const diffDays = (end.getTime() - start.getTime()) / (1000*60*60*24);
  if (diffDays <= 1) return start;

  // Multi-day all-day → afficher dernier jour inclus
  if (end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0) {
    end.setDate(end.getDate() - 1);
  }

  return end;
};



const calculateMaxDuration = (startDate, startTime) => {
  const start = moment(startDate + 'T' + startTime);
  const endOfDay = moment(startDate).endOf('day');
  const maxDurationInMinutes = endOfDay.diff(start, 'minutes');
  return maxDurationInMinutes;
};

const handleStartTimeChange = (e) => {
  const newStartTime = e.target.value;
  const maxDuration = calculateMaxDuration(newLesson.startDate, newStartTime);

  setNewLesson({ ...newLesson, startTime: newStartTime });

  if (duration > maxDuration) {
    setDuration(maxDuration);
    const newEndTime = moment(newLesson.startDate + 'T' + newStartTime)
      .add(maxDuration, 'minutes')
      .format('HH:mm');
    setNewLesson({ ...newLesson, endTime: newEndTime });
  }
};

  const handleDurationChange = (e) => {
    const newDuration = parseInt(e.target.value) || 0;
    const maxDuration = calculateMaxDuration(newLesson.startDate, newLesson.startTime);

    const adjustedDuration = Math.min(newDuration, maxDuration);
    setDuration(adjustedDuration);

    if (newLesson.startTime) {
      const newEndTime = moment(newLesson.startDate + 'T' + newLesson.startTime)
        .add(adjustedDuration, 'minutes')
        .format('HH:mm');
      setNewLesson({ ...newLesson, endTime: newEndTime });
    }
  };

  useEffect(() => {
    if (selectedCategory && newLesson.studentIds && newLesson.studentIds.length > 0 && duration > 0) {
      const price = eventService.calculatePrice(
        selectedCategory,
        newLesson.studentIds.length,
        duration
      );
      setTotalPrice(price);
    } else {
      setTotalPrice(0);
    }
  }, [selectedCategory, newLesson.studentIds, duration]);

  useEffect(() => {
    if (isAllDay && newLesson.startDate) {
      setFormType('event');
    } else if (!isAllDay) {
      setFormType('lesson');
    }
  }, [isAllDay, newLesson.startDate]);
  

  const formatPrice = (price: number) => {
    return price.toFixed(2);
  };

  const handleAddStudent = async (firstname: string, lastname: string, email: string, isInvite: boolean) => {
    if (!firstname.trim() || !lastname.trim()) {
      toast({
        title: t('agenda.studentNameRequired'),
        description: t('agenda.pleaseEnterFullName'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/add_student`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ firstname: firstname.trim(), lastname: lastname.trim(), email: email.trim(), isInvite: isInvite || undefined })
      });

      if (!response.ok) {
        throw new Error('Failed to add student');
      }

      const data = await response.json();

      //await refreshUserData();

      const newStudent = {
        id: data.student.id,
        firstname,
        lastname,
        isInvite,
        email
      };

      /*setNewLesson(prev => ({
        ...prev,
        studentIds: [...prev.studentIds, data.student.id]
      }));*/

      handleStudentAdded(firstname, lastname, data.student.id);

      /*toast({
        title: t('agenda.studentAdded'),
        description: `${firstname} ${lastname} ${t('agenda.addedToStudentsList')}`
      });*/

      //setShowAddStudentForm(false);

    } catch (error) {
      console.error('Error adding student:', error);
      toast({
        title: t('agenda.errorAddingStudent'),
        description: t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    }
  };

  const handleAddCategory = async (name: string, color: string, paymentType: number, prices: Record<string, number>) => {
    if (!name.trim()) {
      toast({
        title: t('agenda.categoryNameRequired'),
        description: t('agenda.pleaseEnterName'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/add_category`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          color: color,
          paymentType: paymentType,
          prices: prices
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add category');
      }

      const data = await response.json();

      await refreshUserData();

      setNewLesson(prev => ({
        ...prev,
        categoryId: data.category.id
      }));

      const newCategoryId = data.category.id;

      handleCategoryAdded(name, color, paymentType, prices, newCategoryId);

      /*toast({
        title: t('agenda.categoryAdded'),
        description: `${name} ${t('agenda.addedToCategoriesList')}`
      });*/

      //setShowAddCategoryForm(false);
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: t('agenda.errorAddingCategory'),
        description: t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    }
  };

  const handleAddLocation = async (name: string) => {
    if (!name.trim()) {
      toast({
        title: t('agenda.locationNameRequired'),
        description: t('agenda.pleaseEnterName'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/add_location`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: name.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to add location');
      }

      const data = await response.json();

      await refreshUserData();

      setNewLesson(prev => ({
        ...prev,
        locationId: data.id
      }));

      handleLocationAdded(name);

      toast({
        title: t('agenda.locationAdded'),
        description: `${name} ${t('agenda.addedToLocationsList')}`
      });

      setShowAddLocationForm(false);
    } catch (error) {
      console.error('Error adding location:', error);
      toast({
        title: t('agenda.errorAddingLocation'),
        description: t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    }
  };

  const getPaymentTypeLabel = (category: any) => {
    if (!category) return '';
    return category.s_std_pay_type === 1 ? t('agenda.fixedPrice') : t('agenda.hourlyRate');
  };

  const handleFormTypeChange = (value: string) => {
    if (value === 'lesson') {
      setFormType('lesson');
      setIsAllDay(false);
    } else {
      setFormType('event');
      setIsAllDay(true);
    }
  };

  const handleRecurrenceToggle = (checked: boolean) => {
    setNewLesson({ ...newLesson, isRecurring: checked });
    if (checked && !newLesson.recurrenceEndDate) {
      const startDate = new Date(newLesson.startDate);
      const endRecurrenceDate = new Date(startDate);
      endRecurrenceDate.setDate(startDate.getDate() + 28);
      setNewLesson({
        ...newLesson,
        isRecurring: checked,
        recurrenceEndDate: format(endRecurrenceDate, 'yyyy-MM-dd')
      });
    }
  };

const submitForm = async (resetAndClose: boolean) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!newLesson.startDate) {
        toast({
          title: t('agenda.missingInformation'),
          description: t('agenda.pleaseSelectDate'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (formType === 'lesson' && (
        newLesson.studentIds.length === 0 ||
        !newLesson.categoryId ||
        !newLesson.startTime ||
        !newLesson.endTime
      )) {
        toast({
          title: t('agenda.missingInformation'),
          description: t('agenda.pleaseSelectRequiredFields'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (formType === 'event' && (eventType === 'special_event' || eventType === 'student_absence') && (
        newLesson.studentIds && newLesson.studentIds.length === 0 
      )) {
        toast({
          title: t('agenda.missingInformation'),
          description: t('agenda.pleaseSelectRequiredFields'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (isAllDay && new Date(newLesson.endDate) < new Date(newLesson.startDate)) {
        toast({
          title: t('agenda.invalidDateRange'),
          description: t('agenda.endDateCannotBeBeforeStartDate'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (newLesson.isRecurring && !newLesson.recurrenceEndDate) {
        toast({
          title: t('agenda.missingInformation'),
          description: t('agenda.pleaseSelectRecurrenceEndDate'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      const payload = {
        ...newLesson,
        isAllDay,
        formType,
        eventType,
        recurrenceType: newLesson.isRecurring ? recurrenceType : undefined,
        duration,
        totalPrice: isAllDay ? 0 : (totalPrice > 0 ? totalPrice : undefined),
      };

      //console.log('Submitting event with payload:', payload);

      const result = await eventService.createLesson(token, payload);

      toast({
        title: formType === 'lesson' ? t('agenda.lessonCreated') : t('agenda.eventCreated'),
        description: formType === 'lesson'
          ? `${t('agenda.newLessonAdded')}${newLesson.isRecurring ? ' ' + t('agenda.asRecurringEvent') : ''}`
          : t('agenda.newEventAdded'),
      });

      if (resetAndClose) {
        setNewLesson({
          title: '',
          studentIds: [],
          categoryId: '',
          locationId: '',
          teacherId: [],
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          isRecurring: false,
          recurrenceEndDate: '',
          note: '',
          price: 0,
          extra: '',
          formType: 'lesson',
        });
        onOpenChange(false);
      }

      if (handleCreateLesson) {
        handleCreateLesson();
      }
    
    } catch (error) {
      console.error("Error creating lesson/event:", error);
      toast({
        title: t('agenda.errorCreatingLesson'),
        description: error instanceof Error ? error.message : t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFilteredStudents = () => {
    if (!searchQuery.trim()) {
      return students; // Retourne TOUJOURS la liste originale si la recherche est vide
    }
    const lowerSearch = searchQuery.toLowerCase();
    return students.filter(student => {
      const fullName = student.name || `${student.firstname} ${student.lastname}`;
      return fullName.toLowerCase().includes(lowerSearch);
    });
  };

  const ownSchool = user?.schools?.find(s => s.school_type === "C");
  const [selectedSchoolId, setSelectedSchoolId] = useState(() => {
    return ownSchool?.id || user?.schools?.[0]?.id || null;
  });

  const filteredStudents = useMemo(() => {
    const filteredStudentsRaw = getFilteredStudents();
    if (
      user?.account_type === "C" &&
      user?.roleType === "teachers_all" &&
      selectedSchoolId
    ) {
      return filteredStudentsRaw.filter(student => {
        if (student.school_origin === "old") {
          // Student de ma propre school
          return selectedSchoolId === ownSchool?.id;
        }
        // Student rattaché à une autre school
        return student.school_id === selectedSchoolId;
      });
    }

    return filteredStudentsRaw;
  }, [searchQuery, students, selectedSchoolId, user, ownSchool]);
  
  const handleStudentSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      
      const filteredStudents = getFilteredStudents();
      
      if (filteredStudents && filteredStudents.length > 0) {
        // Get the first matching student
        const firstMatchedStudent = filteredStudents[0];
        
        // Check if already selected
        const isAlreadySelected = newLesson?.studentIds?.includes(firstMatchedStudent.id);
        
        if (!isAlreadySelected) {
          // Add student to selected list
          const updatedStudentIds = [...(newLesson?.studentIds || []), firstMatchedStudent.id];
          setNewLesson({...newLesson, studentIds: updatedStudentIds});
          
          // Clear search term
          setSearchQuery('');
        }
      }
    }
  };

const getDisplayEndDate = (endDate: string, startDate?: string) => {
  const [year, month, day] = endDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (!startDate) return date;

  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const start = new Date(startYear, startMonth - 1, startDay);

  // Si end > start (multi-day) → retire 1 pour affichage inclusif
  if (date.getTime() > start.getTime()) {
    date.setDate(date.getDate() - 1);
  }

  return date;
};

const getCalendarSelectedEndDate = (endDate: string, startDate: string) => {
  // Affichage dans le mini-calendar : dernier jour sélectionné
  const [year, month, day] = endDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  const start = ymdToLocalDate(startDate);

  // Si multi-day, retire 1 pour le mini-calendar uniquement
  if (date.getTime() > start.getTime()) {
    date.setDate(date.getDate() - 1);
  }

  return date;
};



  const renderSelectedStudents = () => {
    if (!newLesson?.studentIds?.length) {
      return <span className="text-muted-foreground">{t('students.selectStudents')}</span>;
    }
    return (
      <div className="flex flex-wrap gap-1 max-w-full">
        {newLesson.studentIds.map((id: string) => {
          const student = students.find(s => s.id === id); // Utilise `students` ici
          if (!student) return null;
          const displayName = student.name || `${student.firstname} ${student.lastname}`;
          return (
            <div key={id} className="flex items-center mr-1 mb-1">
              <Badge variant="secondary">
                {displayName}
              </Badge>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedStudentIds = newLesson.studentIds.filter(studentId => studentId !== id);
                  setNewLesson({ ...newLesson, studentIds: updatedStudentIds });
                }}
                className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="h-3 w-3" />
              </div>
            </div>
          );
        })}
      </div>
    );
  };



  const renderStudentSelector = () => {
  // ---------------- RENDER ----------------
  return (
    <div className="p-6 w-auto min-h-[38vh]">
      {/* Header */}
      <div className="flex justify-start items-center mb-4">
        <User className="mr-2" />
        <p className="text-lg font-semibold">
          {t('students.selectStudents')}{' '}
          {students.length > 0 ? `(${students.length})` : ''}
        </p>
      </div>

      <div className="grid gap-4">

        {/* SELECTOR SCHOOL si teachers_all */}
        {user?.account_type === "C" && user?.roleType === "teachers_all" && (
         <div className="relative bg-gray-50 p-2 rounded-md">
          <select
            value={selectedSchoolId || ""}
            onChange={(e) => setSelectedSchoolId(Number(e.target.value))}
            className="w-full border rounded-md p-2 pr-10 appearance-none"
          >
            {user?.schools?.map((school) => (
              <option key={school.id} value={school.id}>
                {school.school_name}{school.school_type === "C" ? " (" + t('students.myStudents') + ")" : ""}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        )}


        {/* Search + Add Student */}
        <div className="flex items-center justify-between gap-4 bg-gray-50 p-2 rounded-md">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder={t('agenda.searchStudent')} 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleStudentSearchKeyDown}
              className="pl-8 w-48"
              autoFocus
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowStudentSelector(false);
              setShowAddStudentForm(true);
            }}
            className="h-8 px-2"
          >
            <Plus className="h-3 w-3 mr-1" />
            {t('common.add')}
          </Button>
        </div>

        

        {/* Résultats */}
        <Label className="card">
          {searchQuery.trim()
            ? `${filteredStudents.length > 1
                ? `${filteredStudents.length} ${t('students.founds')}`
                : `${filteredStudents.length} ${t('students.found')}`}`
            : ''}
        </Label>

        {/* LISTE STUDENTS */}
        <div className="max-h-[50vh] overflow-y-auto border rounded-md" key={searchQuery}>
          {filteredStudents.map(student => {
            const isSelected = newLesson?.studentIds?.includes(student.id);
            const displayName = student.name || `${student.firstname} ${student.lastname}`;

            return (
              <div
                key={student.id}
                className={`flex items-center p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-100 ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                onClick={() => {
                  const currentIds = newLesson?.studentIds || [];
                  const newIds = isSelected
                    ? currentIds.filter(id => id !== student.id)
                    : [...currentIds, student.id];
                  setNewLesson({ ...newLesson, studentIds: newIds });
                }}
              >
                <div className={`h-4 w-4 border rounded-sm mr-2 flex items-center justify-center ${
                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className="flex-1">{displayName}</span>
                {user.account_type === "C" && user?.roleType === "teachers_all" && (
                  <div className="ml-2">
                    {student.school_origin === "old" ? <User /> : <Building />}
                  </div>
                )}
              </div>
            );
          })}

          {filteredStudents.length === 0 && (
            <div className="p-4 text-center text-muted-foreground">
              {t('agenda.noStudentFound')}
            </div>
          )}
        </div>

        {/* Footer */}
        {filteredStudents.length > 0 && (
          <div className="flex justify-end items-center space-x-2 mt-4">
            <Label className="flex">
              {t('students.selected')}: {newLesson?.studentIds?.length || 0}
            </Label>
            <Button
              onClick={() => {
                setSearchQuery('');
                setShowStudentSelector(false);
              }}
              disabled={newLesson?.studentIds?.length === 0}
            >
              {t('common.done')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

  const renderMainContent = () => {
    if (showStudentSelector) {
      return renderStudentSelector();
    }

    if (showAddStudentForm) {
      return (
        <div className='flex-1 overflow-auto p-5'>
          <AddStudentForm
            onSubmit={handleAddStudent}
            onCancel={() => setShowAddStudentForm(false)}
          />
        </div>
      );
    }

    if (showAddCategoryForm) {
      return (
        <div className="flex flex-col overflow-auto p-4">
          {showAddCategoryForm && (
            <AddCategoryForm
              onSubmit={handleAddCategory}
              onCancel={() => setShowAddCategoryForm(false)}
            />
          )}
        </div>
      );
    }

    if (showAddLocationForm) {
      return (
        <div className="p-4">
          <AddLocationForm
            onSubmit={handleAddLocation}
            onCancel={() => setShowAddLocationForm(false)}
          />
        </div>
      );
    }

    return (
      <>
        <ScrollArea className={`${isMobile ? "max-h-[calc(100dvh-140px)]" : "max-h-[60vh]"} overflow-auto`}>
          <div className={`grid gap-4 py-4 ${isMobile ? "px-4" : ""}`}>
            <div className="grid gap-2 pt-2 pb-2">
              <RadioGroup
                value={formType}
                onValueChange={handleFormTypeChange}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="lesson" id="lesson" />
                  <Label htmlFor="lesson">{t('agenda.createNewLesson')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="event" id="event" />
                  <Label htmlFor="event">{t('agenda.createEvent')}</Label>
                </div>
              </RadioGroup>
            </div>

            {formType === 'event' && (
              <div className="grid gap-2">
                <Label htmlFor="eventType">{t('agenda.eventType')}</Label>
                <Select
                  value={eventType}
                  onValueChange={(value: 'special_event' | 'student_absence' | 'coach_absence') => setEventType(value)}
                >
                  <SelectTrigger id="eventType">
                    <SelectValue placeholder={t('agenda.selectEventType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="special_event">{t('agenda.specialEvent')}</SelectItem>
                    <SelectItem value="student_absence">{t('agenda.studentAbsence')}</SelectItem>
                    <SelectItem value="coach_absence">{t('agenda.coachAbsence')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {(formType === 'lesson' || (formType === 'event' && eventType !== 'coach_absence')) && (
              <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                {students.length === 0 && renderStudentGuideAnimation()}
                <div className="flex items-center justify-between">
                  <Label htmlFor="student">{t('agenda.students')}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddStudentForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('agenda.addNew')}
                  </Button>
                </div>

                <Button
                  variant="outline"
                  className="w-full justify-start text-left h-auto min-h-10 py-2"
                  onClick={() => setShowStudentSelector(true)}
                >
                  {renderSelectedStudents()}
                </Button>
              </div>
            )}

            {formType === 'lesson' && (
              <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                {students.length > 0 && categories.length === 0 && newLesson.studentIds.length > 0 && !selectedCategory && renderCategoryGuideAnimation()}
                <div className="flex items-center justify-between">
                  <Label htmlFor="category">{t('agenda.category')}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddCategoryForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('agenda.addNew')}
                  </Button>
                </div>

                {selectedCategory ? (
                  <div className="flex items-center gap-2" key={selectedCategory.id}>
                    <Badge className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCategory.color }}></div>
                      {selectedCategory.name}
                    </Badge>
                    <button
                      type="button"
                      className="ml-1 rounded-full outline-none hover:bg-muted"
                      onClick={handleRemoveCategory}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove</span>
                    </button>
                  </div>
                ) : categories.length > 0 ? (
                  <Select
                    value={newLesson.categoryId}
                    onValueChange={(value) => setNewLesson({ ...newLesson, categoryId: value })}
                  >
                    <SelectTrigger id="category" className='text-muted-foreground'>
                      <SelectValue placeholder={t('agenda.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                            {category.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
               ) : (
                <div onClick={() => setShowAddCategoryForm(true)}>
                <Select
                    value={newLesson.categoryId}
                  >
                    <SelectTrigger id="category" className='text-muted-foreground'>
                      <SelectValue placeholder={t('agenda.selectCategory')} />
                    </SelectTrigger>
                    
                  </Select>
                  </div>
              )}
              </div>
            )}

            <div className="grid gap-2">
              {isAllDay ? (
                <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-md p-4">
                  <div className="grid gap-2">
                    <Label htmlFor="start-date">{t('agenda.startDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !newLesson.startDate && "text-muted-foreground"
                          )}
                        >
                            {newLesson.startDate ? (
                                new Intl.DateTimeFormat(
                                  locale === "fr" ? "fr-FR" : "en-US",
                                  { dateStyle: "long", timeZone: "UTC" }
                                ).format(new Date(newLesson.startDate))
                              ) : (
                                <span>{locale === "fr" ? "Choisir une date" : "Pick a date"}</span>
                            )}

                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={newLesson.startDate ? utcToLocalDate(newLesson.startDate) : undefined}
                            onSelect={handleDateSelect}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="end-date">{t('agenda.endDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !newLesson.endDate && "text-muted-foreground"
                          )}
                        >
                           {newLesson.endDate ? (
                              new Intl.DateTimeFormat(
                                locale === 'fr' ? 'fr-FR' : 'en-US',
                                { dateStyle: 'long' }
                              ).format(getDisplayEndDate(newLesson.endDate, newLesson.startDate))
                            ) : (
                              <span>{locale === 'fr' ? 'Choisir une date' : 'Pick a date'}</span>
                            )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                       <Calendar
                          mode="single"
                          selected={newLesson.endDate ? getCalendarSelectedEndDate(newLesson.endDate, newLesson.startDate) : undefined}
                          onSelect={handleDateSelectEnd}
                          disabled={(date) => {
                            if (!newLesson.startDate) return true;
                            const start = ymdToLocalDate(newLesson.startDate);
                            return date < start;
                          }}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md p-4">
                    <div className="grid gap-2">
                      <Label htmlFor="date">{t('agenda.date')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !newLesson.startDate && "text-muted-foreground"
                            )}
                          >
                            {newLesson.startDate ? (
                              new Intl.DateTimeFormat(
                                locale === "fr" ? "fr-FR" : "en-US",
                                { dateStyle: "long", timeZone: "UTC" }
                              ).format(new Date(newLesson.startDate))
                            ) : (
                              <span>{locale === "fr" ? "Choisir une date" : "Pick a date"}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                         <Calendar
                            mode="single"
                            selected={newLesson.startDate ? utcToLocalDate(newLesson.startDate) : undefined}
                            onSelect={handleDateSelect}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="startTime">{t('agenda.startTime')}</Label>
                          <CustomTimePicker
                            value={newLesson.startTime}
                            onChange={(newStartTime) => {
                              const [hours, minutes] = newStartTime.split(":").map(Number);
                              const newStartDateTime = new Date(newLesson.startDate);
                              newStartDateTime.setHours(hours, minutes, 0, 0);

                              const newEndDateTime = new Date(newStartDateTime.getTime() + duration * 60000);
                              const newEndTime = format(newEndDateTime, "HH:mm");

                              handleTimeChange(newStartTime, newEndTime);
                            }}
                          />
                        </div>      
                          <div className="grid gap-2">
                          <Label htmlFor="endTime">{t('agenda.endTime')}</Label>
                          <CustomTimePicker
                            value={newLesson.endTime}
                            onChange={(newEndTime) => {
                              handleTimeChange(newLesson.startTime, newEndTime);
                            }}
                          />
                      </div>
                    </div>

                    <div className="pr-2">
                      {!showDuration ? (
                        <Button
                          variant="ghost"
                          className="p-0 text-sm text-blue-500 underline"
                          onClick={() => setShowDuration(true)}
                        >
                          {t('agenda.adjustDuration')}
                        </Button>
                      ) : (
                        <div className="grid gap-2">
                          <Label htmlFor="duration">
                            {t('agenda.duration')} ({t('agenda.minutes')})
                          </Label>
                          <Input
                            id="duration"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={duration}
                            onChange={handleDurationChange}
                            onFocus={(e) => {
                              e.target.select();
                            }}
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {formType === 'event' && eventType !== 'coach_absence' && eventType !== 'student_absence' && (
                    <div className="grid gap-4 mt-3 border-t p-4">
                      <div className="mt-2 grid gap-2">
                      <Label htmlFor="title">{t('agenda.createNew.price')}</Label>
                      <Input
                        id="price"
                        type="number"
                        inputMode="numeric"
                        value={newLesson.price}
                        className="w-full"
                        onFocus={(e) => {
                          e.target.select();
                        }}
                        onChange={(e) => setNewLesson({ ...newLesson, price: e.target.value })}
                      />
                    </div>
                  <div className="mt-2 grid gap-2 pt-1">
                    <Label htmlFor="title">{t('agenda.createNew.extra')}</Label>
                    <Input
                      id="extra"
                      type="number"
                      value={newLesson.extra}
                      inputMode="numeric"
                      className="w-full"
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      onChange={(e) => setNewLesson({ ...newLesson, extra: e.target.value })}
                    />
                  </div>
                  </div>
              )}

              {formType === 'lesson' && (
              <div className="grid gap-4 mt-3 border-t border-b p-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-recurring"
                    checked={newLesson.isRecurring}
                    onCheckedChange={handleRecurrenceToggle}
                  />
                  <Label htmlFor="is-recurring">{t('agenda.recurring')}</Label>
                </div>

                {newLesson.isRecurring && (
                  <div className="grid gap-4 mt-2 pl-6">
                    <div className="grid gap-2">
                      <Label htmlFor="recurrence-type">{t('agenda.recurrenceType')}</Label>
                      <Select
                        value={recurrenceType}
                        onValueChange={(value: '1' | '2' | '3') => setRecurrenceType(value)}
                      >
                        <SelectTrigger id="recurrence-type">
                          <SelectValue placeholder={t('agenda.selectRecurrenceType')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">{t('agenda.every7Days')}</SelectItem>
                          <SelectItem value="2">{t('agenda.every14Days')}</SelectItem>
                          <SelectItem value="3">{t('agenda.every30Days')}</SelectItem>
                          <SelectItem value="4">{t('agenda.everyWeek')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="recurrence-end-date">{t('agenda.recurrenceEndDate')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !newLesson.recurrenceEndDate && "text-muted-foreground"
                            )}
                          >
                            {newLesson.recurrenceEndDate ? moment(newLesson.recurrenceEndDate).format("LL") : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newLesson.recurrenceEndDate ? new Date(newLesson.recurrenceEndDate) : undefined}
                            onSelect={(date) => date && setNewLesson({ ...newLesson, recurrenceEndDate: format(date, 'yyyy-MM-dd') })}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                            disabled={(date) => {
                              if (!newLesson.startDate) return false;
                              return date < new Date(newLesson.startDate);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
              </div>
              )}


              <div className="grid gap-4 mt-2 p-4">
                {formType === 'lesson' && (
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="location">{t('agenda.location')} <span className="text-xs text-muted-foreground">({t('agenda.optional')})</span></Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAddLocationForm(true)}
                        className="h-8 px-2 text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {t('agenda.addNew')}
                      </Button>
                    </div>

                    {selectedLocation ? (
                      <div className="flex items-center gap-2">
                        <Badge className="flex items-center gap-2">
                          <span>{selectedLocation.name}</span>
                        </Badge>
                        <button
                          type="button"
                          className="ml-1 rounded-full outline-none hover:bg-muted"
                          onClick={handleRemoveLocation}
                        >
                          <X className="h-3 w-3" />
                          <span className="sr-only">Remove</span>
                        </button>
                      </div>
                    ) : (
                      <Select
                        value={newLesson.locationId}
                        onValueChange={(value) => setNewLesson({ ...newLesson, locationId: value })}
                      >
                        <SelectTrigger id="location">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                 <div className="grid gap-2">
                    <Label htmlFor="title">{t('agenda.createNew.title')}</Label>
                    <Input
                      id="title"
                      placeholder={t('agenda.createNew.titlePlaceholder')}
                      type="text"
                      value={newLesson.title}
                      className="w-full"
                      onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                    />
                  </div>
                <div className="grid gap-2 pt-2">
                  <Label htmlFor="notes">{t('agenda.notes')}</Label>
                  <span className='text-xs'>
                    {isAllDay ? t('agenda.createNew.noteInfo') : ''}
                  </span>
                  <Textarea
                    id="notes"
                    placeholder={t('agenda.notesPlaceholder')}
                    value={newLesson.note}
                    onChange={(e) => setNewLesson({ ...newLesson, note: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {formType === 'lesson' && selectedCategory && newLesson.studentIds && newLesson.studentIds.length > 0 && !isAllDay && (
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200 mt-2">

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HandCoins className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium">Pricing</span>
                  </div>
                  <Badge variant="outline" className="font-semibold">
                    Total: ${formatPrice(totalPrice)}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                  <div>{t('agenda.category')}:</div>
                  <div>{selectedCategory.name}</div>

                  <div>{t('agenda.paymentType')}:</div>
                  <div>{getPaymentTypeLabel(selectedCategory)}</div>

                  <div>{t('agenda.numberOfStudents')}:</div>
                  <div>{newLesson.studentIds.length}</div>

                  <div>{t('agenda.duration')}:</div>
                  <div>{duration} {t('agenda.minutes')}</div>
                  
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-100 z-20 pb-8" : "pt-5"}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <div className="flex">
            <div className="relative flex">
              <Button
                onClick={() => submitForm(true)}
                disabled={isSubmitting}
                className="rounded-r-none"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    {t('common.processing')}
                  </span>
                ) : (
                  formType === 'lesson' ? t('agenda.createLesson') : t('agenda.createEvent')
                )}
              </Button>
              <div className="border-l border-gray-300"></div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    className="rounded-l-none px-2"
                    disabled={isSubmitting}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => submitForm(false)} className='cursor-pointer'>
                    {t('agenda.saveAndContinue')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogFooter>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
  <DialogContent
    hideCloseButton={
      showAddStudentForm || showAddCategoryForm || showAddLocationForm || showStudentSelector
    }
    className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}
  >
    <>
      {!showAddStudentForm && !showAddCategoryForm && !showAddLocationForm && !showStudentSelector ? (
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-blue-500" />
              {t('agenda.scheduleLessonTitle')}
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
              {t('agenda.scheduleLessonTitle')}
          </DialogDescription>
        </DialogHeader>
      ) : (
        <div className="flex items-center justify-between px-3 py-2 mb-2 m-2">
          <Button variant="outline" onClick={handleBackClick}>
            <ArrowLeft /> {t('common.back')}
          </Button>
        </div>
      )}

      {renderMainContent()}
    </>
  </DialogContent>
</Dialog>

  );
};



export default NewLessonDialog;
